import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { BASE_URL } from '@/configs';
import router from '@/routers';
import AuthService from '@/services/auth.service';
import { useStorage } from '@/utils/useStorage';

function isBinaryResponse(response: AxiosResponse): boolean {
  const responseType = response.config?.responseType;
  return responseType === 'blob' || responseType === 'arraybuffer';
}

const http = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

http.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const storage = useStorage();
    const accessToken = storage.getItem('accessToken');
    config.headers = config.headers ?? {};
    config.headers['X-AUTH-DOMAIN'] = window.location.hostname;
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

http.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.status === 401) {
      AuthService.clearCache();
      return response;
    }

    if (isBinaryResponse(response)) {
      return response;
    }

    return {
      status: 200,
      ...response.data,
    };
  },
  (error: AxiosError) => {
    switch (error.response?.status) {
      case 401: {
        const suppressUnauthorizedRedirect =
          router.currentRoute.value.meta?.suppressUnauthorizedRedirect === true;
        if (!suppressUnauthorizedRedirect) {
          router.push('/candidate/login');
        }
        break;
      }
      default:
        console.log(error.message);
        break;
    }
    return Promise.reject(error);
  }
);

const request = <T = unknown>(url: string, options: AxiosRequestConfig<unknown>): Promise<T> => {
  return http.request({ ...options, url }) as Promise<T>;
};

export { http };
export default request;
